/**
 * Axios-инстанс с типизированными интерцепторами.
 * Авторизация по токену, Accept-Language и отправка критичных ошибок в Sentry.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useTerminalStore } from '@/stores'
import { i18n } from '@/i18n'
import { captureException } from '@/utils/sentry'

const instance = axios.create()

const CRITICAL_STATUSES = [500, 502, 503, 504]

// ===== REQUEST =====
instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = useTerminalStore().token
        if (token) {
            config.headers.Authorization = 'Token ' + token
        }

        config.headers['Accept-Language'] =
            i18n.global.locale.value || 'ru'

        return config
    },
    (error: unknown) => {
        captureException(error, {
            tags: { stage: 'request' },
        })

        return Promise.reject(error)
    }
)

// ===== RESPONSE =====
instance.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
        const { config, response } = error

        const status = response?.status

        if (!status || !CRITICAL_STATUSES.includes(status)) {
            return Promise.reject(error)
        }

        captureException(error, {
            level: 'error',
            tags: {
                stage: 'response',
                endpoint: config?.url,
                method: config?.method,
                status,
            },
            extra: {
                meta: config?.meta,
                requestData: config?.data,
                responseData: response?.data,
            },
        })

        return Promise.reject(error)
    }
)

export default instance